import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Plane, Car, Briefcase } from 'lucide-react';

const SectionSeparator = ({ isDark }) => (
  <div className="w-full py-8 flex items-center justify-center">
    <div className="flex items-center gap-4 w-full max-w-md">
      <div className={`flex-1 h-px bg-gradient-to-r from-transparent ${isDark ? 'to-[#D4AF37]/30' : 'to-[#D4AF37]/40'}`} />
      <div className="w-2 h-2 rotate-45 bg-[#D4AF37]/60" />
      <div className={`flex-1 h-px bg-gradient-to-l from-transparent ${isDark ? 'to-[#D4AF37]/30' : 'to-[#D4AF37]/40'}`} />
    </div>
  </div>
);

const Services = () => {
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = sectionRef.current?.querySelectorAll('.service-card');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: Plane,
      titleKey: 'services.airport.title',
      descKey: 'services.airport.description',
      testId: 'service-airport',
    },
    {
      icon: Car,
      titleKey: 'services.private.title',
      descKey: 'services.private.description',
      testId: 'service-private',
    },
    {
      icon: Briefcase,
      titleKey: 'services.business.title',
      descKey: 'services.business.description',
      testId: 'service-business',
    },
  ];

  return (
    <section
      id="services"
      ref={sectionRef}
      data-testid="services-section"
      className={`relative py-24 md:py-32 transition-colors duration-500 ${
        isDark ? 'bg-[#0D0D0D]' : 'bg-[#F8F5EE]'
      }`}
    >
      {/* Grain texture */}
      <div 
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Section Title */}
        <div className="text-center mb-16 md:mb-20">
          <h2 
            data-testid="services-title"
            className={`font-playfair text-3xl sm:text-4xl lg:text-5xl mb-6 ${
              isDark ? 'text-white' : 'text-[#1A1A1A]'
            }`}
          >
            {t('services.title')}
          </h2>
          <div className="flex items-center justify-center gap-3">
            <div className={`w-12 h-px ${isDark ? 'bg-[#D4AF37]/50' : 'bg-[#D4AF37]/70'}`} />
            <div className="w-2 h-2 rotate-45 bg-[#D4AF37]" />
            <div className={`w-12 h-px ${isDark ? 'bg-[#D4AF37]/50' : 'bg-[#D4AF37]/70'}`} />
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {services.map((service, index) => (
            <div
              key={index}
              data-testid={service.testId}
              className={`service-card group relative p-8 md:p-10 rounded-sm transition-all duration-500 opacity-0 translate-y-8 ${
                isDark 
                  ? 'bg-gradient-to-b from-[#1A1A1A] to-[#141414] border-2 border-[#D4AF37]/20 hover:border-[#D4AF37]/60' 
                  : 'bg-white border-2 border-[#D4AF37]/30 hover:border-[#D4AF37]/70'
              } hover:shadow-[0_20px_60px_rgba(212,175,55,0.15)] hover:-translate-y-2`}
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              {/* Golden corner accents */}
              <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#D4AF37]/40 rounded-tl-sm" />
              <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#D4AF37]/40 rounded-br-sm" />

              {/* Icon */}
              <div className={`mb-8 inline-flex items-center justify-center w-16 h-16 rounded-full transition-all duration-500 ${
                isDark 
                  ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30 group-hover:bg-[#D4AF37] group-hover:shadow-[0_0_30px_rgba(212,175,55,0.4)]' 
                  : 'bg-[#D4AF37]/15 border border-[#D4AF37]/40 group-hover:bg-[#D4AF37] group-hover:shadow-[0_0_30px_rgba(212,175,55,0.5)]'
              }`}>
                <service.icon className={`w-7 h-7 transition-colors duration-500 ${
                  isDark 
                    ? 'text-[#D4AF37] group-hover:text-[#0A0A0A]' 
                    : 'text-[#D4AF37] group-hover:text-white'
                }`} />
              </div>

              {/* Title */}
              <h3 className={`font-playfair text-xl md:text-2xl mb-4 ${
                isDark ? 'text-white' : 'text-[#1A1A1A]'
              }`}>
                {t(service.titleKey)}
              </h3>

              {/* Description */}
              <p className={`leading-relaxed text-sm md:text-base ${
                isDark ? 'text-[#F8F5EE]/60' : 'text-[#1A1A1A]/60'
              }`}>
                {t(service.descKey)}
              </p>

              {/* Bottom golden line that expands on hover */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent group-hover:w-3/4 transition-all duration-500" />
            </div>
          ))}
        </div>

        <SectionSeparator isDark={isDark} />
      </div>
    </section>
  );
};

export default Services;
